import { IconSidebarSimpleRightSquareFilled } from "@/assets/icons/central/IconSidebarSimpleRightSquareFilled";
import { IconPanelRightOpen } from "@/assets/icons/geist/IconPanelRightOpen";
import { withGitpodSize } from "@/assets/icons/utils";
import { AgentExecutionDropdownMenu } from "@/components/ai/AgentExecutionDropdownMenu";
import {
    ConversationContent,
    ConversationError,
    ConversationInput,
} from "@/components/ai/chat/AgentExecutionConversation";
import { Button } from "@/components/flexkit/Button";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import { cn } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { useDetailsPanelToggleShortcut } from "@/hooks/use-details-panel-toggle-shortcut";
import type { FollowModeState } from "@/hooks/use-follow-mode";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { useOptimisticPendingMessages } from "@/hooks/use-pending-messages";
import { useStreamConversation } from "@/hooks/use-stream-conversation";
import { useStuckConversationDetection } from "@/hooks/use-stuck-conversation-detection";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { agentExecutionName } from "@/utils/agent-executions";
import { ToggleDetailsPanelKeyBinding } from "@/utils/platform";
import { IconSidebarSimpleRightSquare } from "central-icons/IconSidebarSimpleRightSquare";
import { useMemo, type FC } from "react";

const SidebarIconClosed = withGitpodSize(IconSidebarSimpleRightSquare);

export type EnvironmentAgentExecutionConversationProps = {
    agentExecution: PlainAgentExecution | null;
    isDetailsPanelOpen: boolean;
    onToggleDetailsPanel: () => void;
    followMode?: FollowModeState;
    environmentId?: string;
};

export const EnvironmentAgentExecutionConversation = ({
    agentExecution,
    isDetailsPanelOpen,
    onToggleDetailsPanel,
    followMode,
    environmentId,
}: EnvironmentAgentExecutionConversationProps) => {
    const { pendingMessages, handleNewMessage } = useOptimisticPendingMessages();
    const { error, messages, loadingMessage, todoItems, lastAgentBlockContext } = useStreamConversation(
        agentExecution?.id,
        agentExecution?.status?.conversationUrl,
        pendingMessages,
    );

    // Detect stuck conversations and report to Sentry.
    useStuckConversationDetection(agentExecution, lastAgentBlockContext);

    useDetailsPanelToggleShortcut(() => {
        onToggleDetailsPanel();
    });

    return (
        <div
            data-testid="environment-agent-execution-conversation"
            className={cn("flex h-full min-w-[100px] flex-col rounded-l-lg border-border-base bg-surface-01")}
        >
            <Header
                agentExecution={agentExecution}
                isDetailsPanelOpen={isDetailsPanelOpen}
                onToggleDetailsPanel={onToggleDetailsPanel}
                environmentId={environmentId}
            />
            <ConversationError error={error} loadingMessage={loadingMessage} />
            <ConversationContent messages={messages} agentExecution={agentExecution} environmentId={environmentId} />
            <ConversationInput
                hasMessages={messages.length > 0}
                agentExecution={agentExecution}
                onNewMessage={(message) => handleNewMessage(message, messages)}
                todoItems={todoItems}
                followMode={followMode}
                messages={messages}
                environmentId={environmentId}
            />
        </div>
    );
};

const Header: FC<{
    agentExecution: PlainAgentExecution | null;
    isDetailsPanelOpen: boolean;
    onToggleDetailsPanel: () => void;
    environmentId?: string;
}> = ({ agentExecution, isDetailsPanelOpen, onToggleDetailsPanel }) => {
    const displayName = useMemo(
        () => (agentExecution ? agentExecutionName(agentExecution) : "Conversation"),
        [agentExecution],
    );
    const { isMobileViewport: isMobile } = useIsMobileViewport();

    return (
        <div
            className={cn(
                "flex h-12 w-full items-center justify-between gap-2 border-b border-border-subtle px-4 py-2",
            )}
        >
            <div className="flex h-6 min-w-0 flex-1 items-center gap-0.5">
                {agentExecution ? (
                    <AgentExecutionDropdownMenu agentExecution={agentExecution} />
                ) : (
                    <div className="flex min-w-0 items-center gap-1 rounded-md p-1">
                        <span className="min-w-0 truncate text-left text-base font-bold">{displayName}</span>
                    </div>
                )}
            </div>
            <div className="flex items-center justify-end gap-1">
                {isMobile ? (
                    <Tooltip content="Show environment details">
                        <Button
                            variant="ghost"
                            className="rounded-[4px] border-0 p-0"
                            size="lg"
                            onClick={onToggleDetailsPanel}
                            LeadingIcon={IconPanelRightOpen}
                            aria-label="Show environment details"
                            data-tracking-id="show-environment-details-agent-execution-conversation"
                        />
                    </Tooltip>
                ) : (
                    <Tooltip
                        content={
                            <div className="flex flex-row items-center gap-2">
                                <span>{isDetailsPanelOpen ? "Hide details panel" : "Show details panel"}</span>
                                <KeyboardCombo
                                    keys={ToggleDetailsPanelKeyBinding}
                                    className="bg-surface-invert-tertiary"
                                />
                            </div>
                        }
                    >
                        <Button
                            variant="ghost"
                            className={cn("group relative rounded-[4px] border-0 p-2")}
                            size="sm"
                            onClick={onToggleDetailsPanel}
                            aria-label={isDetailsPanelOpen ? "Hide right panel" : "Show right panel"}
                            data-tracking-id="toggle-details-panel-agent-execution-conversation"
                        >
                            <IconSidebarSimpleRightSquareFilled
                                size="sm"
                                className={cn(
                                    "absolute transition-opacity duration-300",
                                    isDetailsPanelOpen
                                        ? "opacity-100 group-hover:opacity-0"
                                        : "opacity-0 group-hover:opacity-100",
                                )}
                            />
                            <SidebarIconClosed
                                size="sm"
                                className={cn(
                                    "transition-opacity duration-300",
                                    isDetailsPanelOpen
                                        ? "opacity-0 group-hover:opacity-100"
                                        : "opacity-100 group-hover:opacity-0",
                                )}
                            />
                        </Button>
                    </Tooltip>
                )}
            </div>
        </div>
    );
};
